/**
 * Chunk Load Error Handler
 *
 * Handles errors that occur when lazy-loaded chunks fail to load after a deployment.
 * This typically happens when:
 * 1. User has the app open during a deployment
 * 2. Service worker has cached references to old chunk hashes
 * 3. User navigates to a lazy-loaded route and the chunk no longer exists
 *
 * The solution: Clear caches and reload automatically with a friendly message.
 */

import { setTakeoverActive } from '@lib/overlay/mutex';
import { logger } from './logger';

// Track if we're already handling a chunk error to prevent loops
let isHandlingChunkError = false;

// Session storage key to prevent reload loops. The same count also rides the reload
// URL (`CHUNK_RELOAD_PARAM`), for environments where sessionStorage is blocked.
const CHUNK_ERROR_RELOAD_KEY = 'meetball_chunk_error_reload';
const MAX_RELOAD_ATTEMPTS = 2;

const CHUNK_RELOAD_PARAM = 'chunk_reload';

/**
 * Parse a persisted attempt count. Anything that isn't a positive integer counts as 0
 * (a corrupted value must not disable or trip the cap).
 */
function parseReloadAttempts(raw: string | null): number {
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

// `?chunk_reload=N`: the count on the reload URL, held in memory. With sessionStorage blocked
// the stored count reads 0 forever, so this is what stops a persistent chunk error looping.
let carried = 0;

/** Reads the URL counter into memory and strips it from the URL. Called once, at boot. */
export function captureChunkReloadParam(): void {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get(CHUNK_RELOAD_PARAM);
  if (raw === null) return;
  carried = parseReloadAttempts(raw);
  url.searchParams.delete(CHUNK_RELOAD_PARAM);
  try {
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch {
    // The count is already held; a URL that keeps the param is cosmetic.
  }
}

/**
 * Check if an error is a chunk/module loading error
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  // Common patterns for chunk loading errors
  const chunkErrorPatterns = [
    'failed to fetch dynamically imported module',
    'loading chunk',
    'loading css chunk',
    'dynamically imported module',
    'failed to load module script',
    'error loading dynamically imported module',
    'importing a module script failed',
    // Network errors during chunk load
    'networkerror when attempting to fetch resource',
    'load failed', // Safari
    // Error names from bundlers (checked against both error.message and error.name)
    'chunkerror', // Vite/Rollup
    'chunkloaderror', // Webpack
  ];

  return chunkErrorPatterns.some(pattern => message.includes(pattern) || name.includes(pattern));
}

/**
 * Check if an error is a MIME type error (HTML returned instead of JS)
 */
export function isMimeTypeError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  return (
    message.includes('mime type') ||
    message.includes('text/html') ||
    message.includes('is not a valid javascript')
  );
}

function getStoredReloadAttempts(): number {
  try {
    return parseReloadAttempts(sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY));
  } catch (e) {
    logger.warn('sessionStorage unavailable for reload tracking', e);
    return 0;
  }
}

/**
 * Get the number of reload attempts in this session.
 * AIDEV-NOTE: max(stored, URL-carried): blocked storage can't count; the URL survives the reload
 */
function getReloadAttempts(): number {
  return Math.max(getStoredReloadAttempts(), carried);
}

/**
 * Increment reload attempts counter
 */
function incrementReloadAttempts(): void {
  try {
    sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, String(getStoredReloadAttempts() + 1));
  } catch (e) {
    logger.warn('sessionStorage unavailable for reload tracking (write)', e);
  }
}

/**
 * Clear reload attempts counter (call on successful app load): the stored count, and
 * the count the reload carried in its URL (captured and stripped at boot).
 */
export function clearReloadAttempts(): void {
  carried = 0;
  try {
    sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
  } catch (e) {
    logger.warn('sessionStorage unavailable for reload tracking (clear)', e);
  }
}

/**
 * Handle chunk load error by clearing caches and reloading
 * Shows a brief "Updating..." message before reload
 */
export async function handleChunkLoadError(error?: Error): Promise<void> {
  // Prevent concurrent handling
  if (isHandlingChunkError) return;
  isHandlingChunkError = true;

  // Check if we've already tried reloading too many times
  const attempts = getReloadAttempts();
  if (attempts >= MAX_RELOAD_ATTEMPTS) {
    logger.error('Chunk load error: Max reload attempts reached', { attempts });
    isHandlingChunkError = false;
    // Let the error boundary handle it with manual reload option
    return;
  }

  logger.info('Handling chunk load error, clearing caches and reloading...', {
    error: error?.message,
    attempt: attempts + 1,
  });

  // Show updating message. It covers everything until the reload, so park the
  // overlay mutex: a surface granted now would open unseen and burn its
  // once-ever mark. Never cleared; the reload resets module state.
  setTakeoverActive('chunk-update', true);
  showUpdatingOverlay();

  // Increment counter before reload
  incrementReloadAttempts();

  try {
    // Clear all caches
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));

    // Unregister service workers
    const serviceWorker = (navigator as Partial<Navigator>).serviceWorker;
    const registrations = serviceWorker ? await serviceWorker.getRegistrations() : [];
    await Promise.all(registrations.map(reg => reg.unregister()));
  } catch (e) {
    logger.error('Error clearing caches during chunk error recovery:', e);
  }

  // Small delay to show the message, then reload
  await new Promise(resolve => setTimeout(resolve, 500));

  // Hard reload with cache busting. Preserve existing query string and
  // hash so deep-link context survives the recovery: share attribution
  // params (`ref_user`, `ref_event`, `ref_question`, `ref_profile`),
  // invite codes (`?invite=…`), and the OAuth callback search params
  // (`?code=…&state=…`).
  const url = new URL(window.location.href);
  url.searchParams.set('v', String(Date.now()));
  url.searchParams.set(CHUNK_RELOAD_PARAM, String(attempts + 1));
  window.location.href = url.toString();
}

/**
 * Show a non-intrusive overlay while updating
 */
function showUpdatingOverlay(): void {
  // Check if overlay already exists
  if (document.getElementById('chunk-update-overlay')) return;

  const overlay = document.createElement('div');
  overlay.id = 'chunk-update-overlay';
  overlay.innerHTML = `
    <div style="
      position: fixed;
      inset: 0;
      background: rgba(240, 239, 235, 0.95);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: var(--z-index-takeover);
      font-family: system-ui, -apple-system, sans-serif;
    ">
      <div style="
        background: white;
        padding: 2rem;
        border-radius: 1rem;
        box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        text-align: center;
        max-width: 300px;
      ">
        <div style="
          width: 40px;
          height: 40px;
          border: 3px solid #e5e5e5;
          border-top-color: #3ec6c6;
          border-radius: 50%;
          margin: 0 auto 1rem;
          animation: spin 1s linear infinite;
        "></div>
        <p style="margin: 0; color: #333; font-weight: 500;">
          Updating to latest version...
        </p>
      </div>
    </div>
    <style>
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    </style>
  `;

  document.body.appendChild(overlay);
}

/**
 * Install global error handlers for chunk loading failures
 * Call this early in app initialization
 */
export function installChunkErrorHandlers(): void {
  // Clear reload counter on successful load
  // This runs after the app has loaded successfully
  window.addEventListener('load', () => {
    // Wait 3 seconds before clearing the reload counter to ensure:
    // 1. All lazy chunks have a chance to load (navigation, code-split routes)
    // 2. The app has stabilized and isn't going to hit another chunk error
    // 3. Any deferred/idle work that might trigger chunk loads has completed
    // If we clear too early and a chunk fails, we'd have no protection against loops
    setTimeout(clearReloadAttempts, 3000);
  });

  // Handle unhandled promise rejections (dynamic imports fail this way)
  window.addEventListener('unhandledrejection', event => {
    const reason: unknown = event.reason;

    if (isChunkLoadError(reason) || isMimeTypeError(reason)) {
      event.preventDefault();
      void handleChunkLoadError(reason instanceof Error ? reason : new Error(String(reason)));
    }
  });

  // Handle regular errors (some chunk errors throw synchronously)
  window.addEventListener('error', event => {
    const err: unknown = event.error;
    if (isChunkLoadError(err) || isMimeTypeError(err)) {
      event.preventDefault();
      void handleChunkLoadError(err instanceof Error ? err : new Error(String(err)));
    }
  });
}
